import { SITE_URL } from '../data/seo'
import { isPhysical, isSoldOut } from '../core/rules/stock'
import type { Product } from '../core/types/catalog'

/**
 * Productfeed voor de Instagram-shop (Meta Commerce Manager), en meteen bruikbaar voor
 * Google Merchant Center: beide lezen RSS 2.0 met de `g:`-velden. Uitleg: docs/INSTAGRAM.md.
 *
 * Dit is de referentie-implementatie. De server levert dezelfde uitvoer op
 * `GET /feeds/products.xml` (LARAVEL.md §13.3), opnieuw gemaakt na elke productwijziging
 * en elke nacht. Meta haalt hem op volgens een schema in Commerce Manager.
 *
 * Regels:
 * - Alleen fysieke producten. Cadeaubonnen mogen niet in een Meta-catalogus (commerce-beleid).
 * - Producten zonder prijs of foto gaan er niet in: Meta keurt ze toch af.
 * - Uitverkocht blijft erin, als `out of stock`. Zo blijven productlabels in oude posts werken.
 * - `id` moet gelijk blijven aan wat Meta nu kent, anders verdwijnen de productlabels uit
 *   bestaande posts. Daarom `retailerIds`: oude id's uit de huidige catalogus (INSTAGRAM.md §4).
 */

export type FeedOptions = {
  siteUrl?: string
  /** Product-id → id zoals Meta hem nu kent (uit de export van Commerce Manager). */
  retailerIds?: Readonly<Record<string, string>>
  /** Moment van maken; start van de actieperiode bij een actieprijs. */
  generatedAt?: Date
}

export type FeedItem = {
  id: string
  title: string
  description: string
  link: string
  imageLink: string
  availability: 'in stock' | 'out of stock'
  condition: 'new'
  /** "29.95 EUR" */
  price: string
  salePrice?: string
  salePriceEffectiveDate?: string
  brand: string
  gtin?: string
  productType: string
  customLabel0?: string
  customLabel1?: string
}

/** Google-productcategorie "Health & Beauty > Personal Care > Cosmetics > Skin Care". Meta leest hem ook. */
export const SKIN_CARE_CATEGORY = 567

const TITLE_MAX = 150
const DESCRIPTION_MAX = 5000

export function feedItems(products: readonly Product[], options: FeedOptions = {}): FeedItem[] {
  const siteUrl = options.siteUrl ?? SITE_URL
  const generatedAt = options.generatedAt ?? new Date()
  return products.filter(isFeedable).map((p) => toItem(p, siteUrl, options.retailerIds?.[p.id], generatedAt))
}

export function productFeedXml(products: readonly Product[], options: FeedOptions = {}): string {
  const siteUrl = options.siteUrl ?? SITE_URL
  const items = feedItems(products, options).map(itemXml).join('\n')
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<rss version="2.0" xmlns:g="http://base.google.com/ns/1.0">',
    '<channel>',
    '  <title>Beauty Bewust</title>',
    `  <link>${escapeXml(siteUrl)}</link>`,
    '  <description>Huidverzorging uit de salon van Beauty Bewust</description>',
    items,
    '</channel>',
    '</rss>',
    '',
  ].join('\n')
}

/** Komt dit product in de feed (en is het dus te taggen in Instagram)? Ook gebruikt door de social planner. */
export function isFeedable(p: Product): boolean {
  return isPhysical(p) && p.price > 0 && p.image.trim() !== ''
}

function toItem(p: Product, siteUrl: string, retailerId: string | undefined, generatedAt: Date): FeedItem {
  const onSale = p.regularPrice !== undefined && p.regularPrice > p.price
  const item: FeedItem = {
    id: retailerId ?? p.id,
    title: title(p),
    description: description(p),
    link: `${siteUrl}/webshop/${encodeURIComponent(p.id)}`,
    imageLink: absolute(p.image, siteUrl),
    availability: isSoldOut(p) ? 'out of stock' : 'in stock',
    condition: 'new',
    price: money(onSale ? (p.regularPrice as number) : p.price),
    brand: p.brand,
    productType: ['Huidverzorging', p.category].join(' > '),
  }
  if (onSale) {
    item.salePrice = money(p.price)
    if (p.promo) item.salePriceEffectiveDate = `${isoMinute(generatedAt)}/${p.promo.endsOn}T23:59+01:00`
  }
  if (p.gtin?.trim()) item.gtin = p.gtin.trim()
  if (p.step) item.customLabel0 = p.step
  const highlight = p.bestseller ? 'Bestseller' : p.salonFavourite ? 'Salonfavoriet' : undefined
  if (highlight) item.customLabel1 = highlight
  return item
}

/** "Medik8 Crystal Retinal 6 · 30 ml": merk ervoor tenzij de naam er al mee begint. */
function title(p: Product): string {
  const base = p.name.toLowerCase().startsWith(p.brand.toLowerCase()) ? p.name : `${p.brand} ${p.name}`
  const full = p.volume ? `${base} · ${p.volume}` : base
  return full.length > TITLE_MAX ? `${full.slice(0, TITLE_MAX - 1).trimEnd()}…` : full
}

function description(p: Product): string {
  const text = [p.note, p.why].map((s) => s.replace(/\s+/g, ' ').trim()).filter(Boolean).join(' ')
  const fallback = text || `${p.brand} ${p.name}, geadviseerd door Beauty Bewust.`
  return fallback.length > DESCRIPTION_MAX ? `${fallback.slice(0, DESCRIPTION_MAX - 1).trimEnd()}…` : fallback
}

const money = (euros: number) => `${(Math.round(euros * 100) / 100).toFixed(2)} EUR`

const absolute = (url: string, siteUrl: string) => (/^https?:\/\//.test(url) ? url : `${siteUrl}${url.startsWith('/') ? '' : '/'}${url}`)

/** "2026-10-02T09:30+00:00" (UTC; Meta en Google accepteren een expliciete offset). */
const isoMinute = (d: Date) => `${d.toISOString().slice(0, 16)}+00:00`

const escapeXml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;')

function itemXml(item: FeedItem): string {
  const fields: [string, string | undefined][] = [
    ['g:id', item.id],
    ['g:title', item.title],
    ['g:description', item.description],
    ['g:link', item.link],
    ['g:image_link', item.imageLink],
    ['g:availability', item.availability],
    ['g:condition', item.condition],
    ['g:price', item.price],
    ['g:sale_price', item.salePrice],
    ['g:sale_price_effective_date', item.salePriceEffectiveDate],
    ['g:brand', item.brand],
    ['g:gtin', item.gtin],
    ['g:identifier_exists', item.gtin ? undefined : 'no'],
    ['g:google_product_category', String(SKIN_CARE_CATEGORY)],
    ['g:product_type', item.productType],
    ['g:custom_label_0', item.customLabel0],
    ['g:custom_label_1', item.customLabel1],
  ]
  const body = fields
    .filter((f): f is [string, string] => f[1] !== undefined)
    .map(([tag, value]) => `    <${tag}>${escapeXml(value)}</${tag}>`)
    .join('\n')
  return `  <item>\n${body}\n  </item>`
}
